import { Box } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useRequestsContext } from "@/contexts/RequestsContext";
import { backendService } from "@/services/backend.service";
import { DelayHoursField } from "@/ui/Inputs/DelayHoursField";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { parseDelayHours } from "@/utils/agencyPostDelay";
import type { AgencySource } from "@/utils/models";

export function AgencyTargetDelayField({ requestId, source, savedHours }: { requestId: string; source: AgencySource; savedHours: number }) {
	const { t } = useTranslation();
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const { refreshRequests } = useRequestsContext();
	const { track, trackError } = useAnalytics();
	const [hours, setHours] = useState(String(savedHours));
	const mutation = useMutation({
		mutationFn: (nextHours: number) => backendService.updateAgencyTargetDelay(requestId, chosenDepartment.department_id, source, nextHours),
		onMutate: () => track("agency_target_delay_start", { request_id: requestId, agency_source: source }),
		onSuccess: async () => {
			track("agency_target_delay_success", { request_id: requestId, agency_source: source });
			await refreshRequests();
		},
		onError: (error: Error) => {
			trackError("agency_target_delay_error", { request_id: requestId, agency_source: source, error_message: error.message });
			showErrorSnackbar(t("error.updating.agency.post.delay"), { action: "update agency delay" }, error);
		},
	});
	const parsed = parseDelayHours(hours);
	return (
		<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mt: 1 }}>
			<DelayHoursField
				value={hours}
				onChange={setHours}
				suffixLabel={t("hours.before.shift.start")}
				dataTestId={`agency-delay-${source}`}
				disabled={mutation.isPending}
			/>
			<SystemButton
				disabled={parsed === null || parsed === savedHours || mutation.isPending}
				onClick={() => {
					if (parsed !== null) mutation.mutate(parsed);
				}}
				data-testid={`agency-delay-${source}-save`}
			>
				{t("save")}
			</SystemButton>
		</Box>
	);
}
